import { graphql, type HeadProps, type PageProps } from 'gatsby'
import React, { type ReactElement } from 'react'

import Cards from '@/components/card/cards'
import Header from '@/components/header/header'
import Pagination from '@/components/pagination/pagination'
import { Microdata } from '@/components/seo/microdata'
import Seo from '@/components/seo/seo'
import Layout from '@/layouts/layout'
import { sortRelatedContent } from '@/utils/sort'

type DataProps = {
  allDatoCmsPctPost: Queries.TagQuery['allDatoCmsPctPost']
  allDatoCmsPctInterview: Queries.TagQuery['allDatoCmsPctInterview']
  allDatoCmsPctPaper: Queries.TagQuery['allDatoCmsPctPaper']
  allDatoCmsPctDialog: Queries.TagQuery['allDatoCmsPctDialog']
  allDatoCmsPctProject: Queries.TagQuery['allDatoCmsPctProject']
  allDatoCmsPctEpisode: Queries.TagQuery['allDatoCmsPctEpisode']
  allDatoCmsPctPage: Queries.TagQuery['allDatoCmsPctPage']
  allDatoCmsPctEvent: Queries.TagQuery['allDatoCmsPctEvent']
  allDatoCmsPctExternalContent: Queries.TagQuery['allDatoCmsPctExternalContent']
  datoCmsPctTag: Queries.TagQuery['datoCmsPctTag']
}

type PageContextProps = {
  limit: number
  skip: number
  numPages: number
  currentPage: number
  basePath: string
  totalItems: number
}

export const Head: React.FC<HeadProps<DataProps, PageContextProps>> = ({
  data: { datoCmsPctTag },
  location,
  pageContext: { currentPage }
}): ReactElement => {
  return (
    <>
      <Seo
        data={datoCmsPctTag}
        location={location}
        title={`${datoCmsPctTag?.externalName} ${
          currentPage > 1 ? `(${currentPage})` : ''
        }`}
      />
      <Microdata data={datoCmsPctTag} location={location} />
    </>
  )
}

const Tag: React.FC<PageProps<DataProps, PageContextProps>> = ({
  pageContext,
  data: {
    datoCmsPctTag,
    allDatoCmsPctPost,
    allDatoCmsPctInterview,
    allDatoCmsPctPaper,
    allDatoCmsPctDialog,
    allDatoCmsPctProject,
    allDatoCmsPctEpisode,
    allDatoCmsPctPage,
    allDatoCmsPctExternalContent,
    allDatoCmsPctEvent
  },
  location
}) => {
  const items = sortRelatedContent({
    nodesGroups: [
      [
        ...allDatoCmsPctPost.nodes,
        ...allDatoCmsPctInterview.nodes,
        ...allDatoCmsPctPaper.nodes,
        ...allDatoCmsPctDialog.nodes,
        ...allDatoCmsPctProject.nodes,
        ...allDatoCmsPctEpisode.nodes,
        ...allDatoCmsPctPage.nodes,
        ...allDatoCmsPctExternalContent.nodes,
        ...allDatoCmsPctEvent.nodes
      ]
    ]
  })
  if (datoCmsPctTag == null) return <></>
  if (pageContext == null) return <></>

  const { numPages, currentPage, basePath, totalItems } = pageContext
  const { externalName, description } = datoCmsPctTag
  return (
    <Layout location={location}>
      <Header title={externalName ?? ''} subtitle={description ?? ''} />
      <Cards items={items} numberTotal={totalItems} />
      <Pagination
        currentPage={currentPage}
        numPages={numPages}
        basePath={basePath}
      />
    </Layout>
  )
}

export default Tag

export const query = graphql`
  fragment TagReference on DatoCmsPctTag {
    __typename
    originalId
    id
    externalName
    slug
  }
  fragment Tag on DatoCmsPctTag {
    __typename
    id
    externalName
    slug
    description
    seo {
      title
      description
    }
    seoMetaTags {
      ...GatsbyDatoCmsSeoMetaTags
    }
  }
  query Tag($id: String = "", $contents: [String]) {
    datoCmsPctTag(id: { eq: $id }) {
      __typename
      originalId
      meta {
        firstPublishedAt
        publishedAtTimestamp: publishedAt(formatString: "X")
      }
      externalName
      description
      seo {
        title
        description
        image {
          url
        }
      }
      seoMetaTags {
        ...GatsbyDatoCmsSeoMetaTags
      }
    }

    allDatoCmsPctPost(filter: { id: { in: $contents } }) {
      nodes {
        ...PctPostCard
      }
    }
    allDatoCmsPctInterview(filter: { id: { in: $contents } }) {
      nodes {
        ...PctInterviewCard
      }
    }
    allDatoCmsPctPaper(filter: { id: { in: $contents } }) {
      nodes {
        ...PctPaperCard
      }
    }
    allDatoCmsPctDialog(filter: { id: { in: $contents } }) {
      nodes {
        ...PctDialogCard
      }
    }
    allDatoCmsPctProject(filter: { id: { in: $contents } }) {
      nodes {
        ...PctProjectCard
      }
    }
    allDatoCmsPctEpisode(filter: { id: { in: $contents } }) {
      nodes {
        ...PctEpisodeCard
      }
    }
    allDatoCmsPctPage(filter: { id: { in: $contents } }) {
      nodes {
        ...PctPageCard
      }
    }
    allDatoCmsPctExternalContent(
      filter: { id: { in: $contents }, showInWebsite: { eq: true } }
    ) {
      nodes {
        ...PctExternalContentCard
      }
    }
    allDatoCmsPctEvent(filter: { id: { in: $contents } }) {
      nodes {
        ...PctEventCard
      }
    }
  }
`
